// DashboardDoctor.jsx (refactored)
// MUI-only. Filters fix, reactive centre options, safe args merging.

import React, { useEffect, useRef, useContext, useState, useMemo } from "react";
import { Helmet } from "react-helmet";
import moment from "moment";
import { format } from "date-fns";
import { RequireAuth } from "../../RequireAuth";

// components
import StateDistrictFilter from "./geographyFilter";
import DashboardKPISection from "./DashboardKPI";

// Recharts
import {
  ResponsiveContainer,
  LineChart as RLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  BarChart,
  Bar,
  Tooltip as RechartsTooltip,
  Area,
} from "recharts";

// Icons
import { Icon } from "@iconify/react";

// APIs & Context
import { AuthContext } from "../../context/AuthContextProvider";
import { useGetAnalytics, useGetTimeseries } from "../../api/analytics";

// Google Maps
import {
  GoogleMap,
  useJsApiLoader,
  MarkerF,
  InfoWindowF,
} from "@react-google-maps/api";
import { MAP_LIBRARIES } from "../../utils/mapLibraries";

// MUI
import {
  Box,
  Typography,
  Button,
  Skeleton,
  FormControl,
  Select,
  MenuItem,
  Divider,
  Popover,
  Grid,
  ListItemText,
  Checkbox,
  useMediaQuery,
  useTheme,
  Fade,
  Tooltip,
} from "@mui/material";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";

// MUI X Date pickers
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

// MUI X-Charts
import { PieChart } from "@mui/x-charts/PieChart";

// Assets
import pieChartloading from "../../assets/icons/pieChartloading.svg";
import barChartLoading from "../../assets/icons/barChartLoading.svg";
import barsLoading from "../../assets/icons/barsLoading.svg";

//Dashboard components
import DashboardPieSection from "./DashboardPiechart";
import DashboardTestsLine from "./DashboardTestLine";
import DashboardCasesBar from "./DashboardCasesBar";
import DashboardFiltersRow, {
  DashboardFilterActions,
} from "./DashboardFilterRow";
import { BlankPreview } from "../BlankPreview";
import nopreview from "../../assets/images/others/noauth.svg";

const GOOGLE_MAPS_API_KEY = "AIzaSyC2k1lKoyNg_Kj5SoC2z2hCROopjGxB93Y";

const INTERPRETATION_COLORS = {
  Normal: "#53B253",
  Abnormal: "#FFC46C",
  Critical: "#FB8588",
  Retake: "#C2C9FF",
};

const containerStyle = {
  width: "100%",
  height: "310px",
  borderRadius: "1rem",
};

const buildLineData = (timeSeries, granularity) =>
  (timeSeries?.reports || []).map((entry) => {
    const dateStr = entry.day || entry.hour || entry.week || entry.month;
    const date = moment(dateStr);
    let x = "";
    if (granularity === "hour") x = date.format("HH:mm");
    else if (granularity === "day") x = date.format("DD MMM");
    else if (granularity === "week") x = date.format("DD MMM");
    else if (granularity === "month") x = date.format("MMM YY");
    else x = date.format("DD/MM/YYYY");

    const counts = entry.statusCounts || entry.counts || {};
    const normal = Number(counts.normal || 0);
    const abnormal = Number(counts.abnormal || 0);
    const critical = Number(counts.critical || 0);
    const retake = Number(counts.retake || 0);
    const beyond_critical =
      Number(counts.beyond_critical || 0) +
      Number(counts["beyond critical"] || 0);
    const total = normal + abnormal + critical + retake + beyond_critical;
    return { x, normal, abnormal, critical, retake, total, beyond_critical };
  });

const buildBarData = (timeSeries, casesGranularity) =>
  (timeSeries?.cases || []).map((entry) => {
    const dateStr = entry.date;
    const date = moment(dateStr);
    let x = "";
    if (casesGranularity === "hour") x = date.format("HH:mm");
    else if (casesGranularity === "day") x = date.format("DD MMM");
    else if (casesGranularity === "week") x = date.format("DD MMM");
    else if (casesGranularity === "month") x = date.format("MMM YY");
    else x = date.format("DD/MM/YYYY");
    return { x, count: entry.count || 0 };
  });

const buildCentreData = (analyticsData) => {
  const groupedByCenter = analyticsData?.groupedByCenter || [];
  const centerDetails = analyticsData?.centerDetails || {};
  return groupedByCenter.map((entry) => {
    const [centerId, statusAndCount] = Object.entries(entry)[0] || [];
    const name = centerDetails[centerId]?.name || "UNKNOWN";
    const {
      normal = 0,
      abnormal = 0,
      critical = 0,
      retake = 0,
    } = statusAndCount || {};
    return {
      id: centerId,
      name,
      normal,
      abnormal,
      critical,
      retake,
      total: normal + abnormal + critical + retake,
    };
  });
};

export default function DashboardDoctor() {
  // -------------------- Context --------------------
  const {
    all_centres = [],
    allCenterIds = [],
    onReceivePushNotification,
  } = useContext(AuthContext);

  // ---------- Centres derived reactively from context ----------
  const centreOptions = useMemo(() => {
    if (!Array.isArray(all_centres) || all_centres.length === 0) return [];
    return all_centres.flatMap((hub) => {
      const hubOption = {
        label: hub?.label ?? hub?.name ?? "Unnamed Hub",
        value: hub?.value ?? hub?.id,
      };
      const spokeOptions =
        hub.spokeCenters?.map((spoke) => ({
          label: spoke?.name ?? "Unnamed Spoke",
          value: spoke?.id,
        })) || [];
      return [hubOption, ...spokeOptions];
    });
  }, [all_centres]);

  const [allCentres, setAllCentres] = useState([]);
  const [selectedCentres, setSelectedCentres] = useState([]);

  // MUI Select granularity (Tests + Cases)
  const [granularityState, setGranularityState] = useState({
    value: "hour",
    label: "Hourly",
    options: [],
  });

  const [casesGranularityState, setCasesGranularityState] = useState({
    value: "hour",
    label: "Hourly",
    options: [],
  });
  const [selectedDateFilter, setSelectedDateFilter] = useState("today");

  const [selectedDateTitle, setSelectedDateTitle] = useState("Today");

  const [markers, setMarkers] = useState([]);
  const [selectedMarkerId, setSelectedMarkerId] = useState(null);
  const [geoFilter, setGeoFilter] = useState({ state: "", district: "" });

  //refersh args to refresh the page
  const [refreshArg, setRefreshArg] = useState(false);

  // Date popover + custom From/To
  const [dateAnchorEl, setDateAnchorEl] = useState(null);
  const dateMenuOpen = Boolean(dateAnchorEl);
  const handleOpenDateMenu = (e) => setDateAnchorEl(e.currentTarget);
  const handleCloseDateMenu = () => setDateAnchorEl(null);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  // keep args minimal initially; we'll sync it with UI changes in an effect
  const [args, setArgs] = useState({
    center_ids: allCenterIds && allCenterIds.length > 0 ? allCenterIds : [],
    granularity: granularityState.value,
    cases_granularity: casesGranularityState.value,
    filter: selectedDateFilter.toLowerCase(),
    state: geoFilter.state || "",
    district: geoFilter.district || "",
  });

  // API hooks (will re-run on args changes)
  const { analytics: analyticsData, isLoading } = useGetAnalytics({
    ...args,
    refreshArg,
  });
  const { timeSeries, isLoading: isLoadingTimeseries } = useGetTimeseries({
    ...args,
    refreshArg,
  });

  // -------------------- Chart data (seeded from cache so a remount with --------------------
  // cached SWR data doesn't briefly render empty charts before the effect below runs)
  const [lineData, setLineData] = useState(() =>
    isLoading || isLoadingTimeseries
      ? []
      : buildLineData(timeSeries, granularityState.value),
  );
  const [barData, setBarData] = useState(() =>
    isLoading || isLoadingTimeseries
      ? []
      : buildBarData(timeSeries, casesGranularityState.value),
  );
  const [, setCentreData] = useState(() =>
    isLoading || isLoadingTimeseries ? [] : buildCentreData(analyticsData),
  );

  // callback passed into GeographyFilter. When user picks state/district this will be called.
  const onGeographyChange = (stateValue = "", districtValue = "") => {
    setGeoFilter({ state: stateValue || "", district: districtValue || "" });
  };

  // media query
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));

  // refs
  const mapRef = useRef(null);

  // Google Maps loader
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: MAP_LIBRARIES,
  });

  // user
  const [user, setUser] = useState({});
  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("@auth_login")) || {};
    setUser(storedUser?.data || {});
  }, []);

  // -------------------- Permission Check --------------------
  const hasCasesAccess = Boolean(user?.permissions?.cases?.canViewCases);
  const hasInterpretationAccess = Boolean(
    user?.permissions?.ecgInterpretation?.canInterpreteReport,
  );
  const hasDashboardAccess = hasCasesAccess || hasInterpretationAccess;

  //push notification
  useEffect(() => {
    if (onReceivePushNotification) {
      setRefreshArg((prev) => !prev);
    }
  }, [onReceivePushNotification]);

  // keep local centre list + default selected centres in sync with context options
  useEffect(() => {
    setAllCentres(centreOptions);
    // default select all if nothing chosen previously
    setSelectedCentres((prev) =>
      prev && prev.length > 0 ? prev : [...centreOptions],
    );
  }, [centreOptions]);

  // ensure args has center_ids when allCenterIds from auth context becomes available
  useEffect(() => {
    if (Array.isArray(allCenterIds) && allCenterIds.length > 0) {
      setArgs((prev) => ({ ...prev, center_ids: allCenterIds }));
    }
  }, [allCenterIds]);

  // Build markers from context hubs/spokes
  useEffect(() => {
    if (!Array.isArray(all_centres) || all_centres.length === 0) {
      setMarkers([]);
      return;
    }

    const extractedMarkers = all_centres.flatMap((hub) => {
      const arr = [];
      const hubLat = parseFloat(hub?.latitude);
      const hubLng = parseFloat(hub?.longitude);
      if (!isNaN(hubLat) && !isNaN(hubLng)) {
        arr.push({
          id: hub?.id || hub?.value,
          name: hub?.name || hub?.label || "Unknown Hub",
          lat: hubLat,
          lng: hubLng,
          address: hub?.fullAddress || "No Address",
          type: "Hub",
        });
      }
      if (Array.isArray(hub.spokeCenters)) {
        hub.spokeCenters.forEach((spoke) => {
          const coords = spoke?.address?.location?.coordinates || [];
          const lng = parseFloat(coords[0]);
          const lat = parseFloat(coords[1]);
          if (!isNaN(lat) && !isNaN(lng)) {
            arr.push({
              id: spoke?.id,
              name: spoke?.name || "Unnamed Spoke",
              lat,
              lng,
              address:
                spoke?.address?.fulladdress ||
                `${spoke?.address?.area || ""}, ${spoke?.address?.city || ""}`,
              type: "Spoke",
            });
          }
        });
      }
      return arr;
    });
    setMarkers(extractedMarkers);
  }, [all_centres]);

  // Select all centres by default once options are available (safety)
  const setCentresOnceRef = useRef(false);

  useEffect(() => {
    if (setCentresOnceRef.current) return;

    if (allCentres.length > 0) {
      setSelectedCentres(allCentres);
      setCentresOnceRef.current = true;
    }
  }, [allCentres]);

  // -------------------- Granularity rules --------------------
  useEffect(() => {
    let value = "hour";
    let label = "Hourly";
    let options = [];

    const isCustom = selectedDateFilter === "custom" && fromDate && toDate;

    if (!isCustom) {
      if (selectedDateFilter === "today") {
        value = "hour";
        label = "Hourly";
      } else if (selectedDateFilter === "last_7_days") {
        value = "day";
        label = "Daily";
      } else if (selectedDateFilter === "last_30_days") {
        value = "week";
        label = "Weekly";
        options = [
          { label: "Weekly", value: "week" },
          { label: "Daily", value: "day" },
        ];
      } else if (selectedDateFilter === "last_3_months") {
        value = "week";
        label = "Weekly";
        options = [
          { label: "Weekly", value: "week" },
          { label: "Monthly", value: "month" },
        ];
      } else if (
        selectedDateFilter === "last_6_months" ||
        selectedDateFilter === "last_12_months"
      ) {
        value = "month";
        label = "Monthly";
      }
    } else {
      const diffDays = Math.max(
        0,
        Math.round(
          (new Date(toDate) - new Date(fromDate)) / (1000 * 60 * 60 * 24),
        ),
      );
      if (diffDays <= 7) {
        value = "day";
        label = "Daily";
      } else if (diffDays <= 30) {
        value = "week";
        label = "Weekly";
      } else {
        value = "month";
        label = "Monthly";
      }
    }

    setGranularityState({ value, label, options });
    setCasesGranularityState({ value, label, options });

    if (selectedDateFilter === "custom" && fromDate && toDate) {
      // we will write start/end to args in the args-sync effect below
    }
  }, [selectedDateFilter, fromDate, toDate]);

  // -------------------- Keep args in sync with UI filters --------------------
  useEffect(() => {
    const centerIdsSelected = (selectedCentres || [])
      .map((c) => c.value);

    const isAllSelected =
      centerIdsSelected.length === allCentres.length;

    const center_ids = isAllSelected ? null : centerIdsSelected;

    const newArgs = {
      ...args,
      center_ids,
      granularity: granularityState.value,
      cases_granularity: casesGranularityState.value,
      filter: (selectedDateFilter || "today").toLowerCase(),
      state: geoFilter.state || "",
      district: geoFilter.district || "",
    };

    if (selectedDateFilter === "custom" && fromDate && toDate) {
      newArgs.start = new Date(fromDate).toISOString();
      newArgs.end = new Date(toDate).toISOString();
    } else {
      delete newArgs.start;
      delete newArgs.end;
    }

    const prevKeys = Object.keys(args).sort();
    const newKeys = Object.keys(newArgs).sort();
    const same =
      prevKeys.length === newKeys.length &&
      prevKeys.every((k) => newArgs[k] === args[k]);

    if (!same) {
      setArgs(newArgs);
    }
  }, [
    selectedCentres,
    allCentres, //  IMPORTANT (needed for comparison)
    granularityState.value,
    casesGranularityState.value,
    selectedDateFilter,
    geoFilter.state,
    geoFilter.district,
    fromDate,
    toDate,
  ]);

  // -------------------- Manual refresh (force revalidation) --------------------
  const handleRefresh = () => {
    // merge a timestamp to force hooks to refetch
    setArgs((prev) => ({ ...prev, __refresh: Date.now() }));
  };

  // Generate unique color dynamically for each center
  // --- Purple/blue themed palette (like your image) ---

  // Helpers
  const hashString = (str) => {
    let h = 0;
    for (let i = 0; i < str.length; i++) {
      // eslint-disable-next-line no-bitwise
      h = (h << 5) - h + str.charCodeAt(i);
      h |= 0;
    }
    return Math.abs(h);
  };

  // Convert HSL string (modern CSS) — Chart will accept HSL; if you need HEX we can convert.
  const hsl = (hue, sat, light) => `hsl(${hue}deg ${sat}% ${light}%)`;

  // Palette generator: fixed base hue (purple/blue), vary lightness in a pleasant band
  const makePurplePaletteColor = (id, index, cache) => {
    if (cache.has(id)) return cache.get(id);

    const baseHue = 250; // tweak this between ~230-270 for more blue/purple
    const hash = hashString(String(id || index));

    // create a deterministic step 0..n-1 from hash
    const step = hash % 8; // up to 8 distinct shades; increase if you expect >8 centers

    // Map step to lightness & saturation to mirror the ring image:
    // darker = bigger slice feel; lighter = subtle background slices
    const lightnessSteps = [36, 44, 52, 60, 68, 76, 82, 88];
    const saturationSteps = [78, 76, 74, 72, 70, 68, 66, 64];

    const light = lightnessSteps[step % lightnessSteps.length];
    const sat = saturationSteps[step % saturationSteps.length];

    const color = hsl(baseHue, sat, light);
    cache.set(id, color);
    return color;
  };

  // Build pieSeriesData (Version 1 style: skip items with no name)
  const allCases = analyticsData?.allCasesCenterWise ?? [];
  const centerDetails = analyticsData?.centerDetails ?? {};
  const colorCache = new Map();

  const pieSeriesData = (allCases || [])
    .map((item, index) => {
      const id = item._id ?? String(index);
      const name =
        (centerDetails[id] && centerDetails[id].name) ||
        item.centerName ||
        allCentres.find((c) => c.value === id)?.label;

      if (!name) return null; // skip entries without API-provided name

      const color = makePurplePaletteColor(id, index, colorCache);

      return {
        id,
        value: item.count ?? 0,
        label: name,
        color,
      };
    })
    .filter(Boolean);

  const handleClearFilters = () => {
    setSelectedDateFilter("today");
    setSelectedDateTitle("Today");
    setFromDate(null);
    setToDate(null);

    setGeoFilter({ state: "", district: "" });

    setSelectedCentres(allCentres); // reset to ALL
  };

  const isDefault =
    selectedDateFilter === "today" &&
    !fromDate &&
    !toDate &&
    !geoFilter.state &&
    !geoFilter.district &&
    selectedCentres.length === allCentres.length;
  // -------------------- CSV Export --------------------
  const handleExportCSV = () => {
    if (!analyticsData) return;

    const summaryData = [
      ["Summary"],
      ["Total Cases Refferred", analyticsData.casesReferred ?? "-"],
      ["Total Cases Closed", analyticsData.casesClosed ?? "-"],
      ["Total Test Received", analyticsData.testsReceived ?? "-"],
      ["Total Test Interpreted", analyticsData.totalInterpretedCount ?? "-"],
      [
        "Avg Interpretation Time (min)",
        Math.floor((analyticsData.avgInterpretationTime ?? 0) / 60000),
      ],
      [],
    ];

    const interpretationData = [
      ["Test Results"],
      ["Status", "Count"],
      ...(analyticsData.interpretationStatusCounts
        ? Object.entries(analyticsData.interpretationStatusCounts).map(
          ([label, value]) => [
            label.charAt(0).toUpperCase() + label.slice(1),
            value,
          ],
        )
        : []),
      [],
    ];

    const refferalData = [
      ["Referred Cases"],
      ["Center", "Count"],
      ...(Array.isArray(analyticsData?.allCasesCenterWise)
        ? analyticsData.allCasesCenterWise.map((item, index) => [
          item.centerName || `Center ${index + 1}`,
          item.count ?? 0,
        ])
        : []),
      [],
    ];

    const LineTable = [
      ["Tests"],
      ["Total", "Normal", "Abnormal", "Critical", "Retake"],
      ...lineData.map((l) => [
        l.total,
        l.normal,
        l.abnormal,
        l.critical,
        l.retake,
      ]),
      [],
    ];

    const barTable = [
      ["Cases"],
      ["Date", "Count"],
      ...barData.map((b) => [b.x, b.count]),
      [],
    ];

    const fullData = [
      ...summaryData,
      ...interpretationData,
      ...refferalData,
      ...LineTable,
      ...barTable,
    ];

    const csvContent = fullData.map((row) => row.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    const today = new Date().toISOString().split("T")[0];
    link.setAttribute("download", `Analytics_Report_${today}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const DropdownIcon = (props) => (
    <Icon
      icon="eva:arrow-ios-downward-fill"
      width="20"
      height="20"
      {...props}
    />
  );
  // -------------------- Tooltips --------------------
  const CustomTooltip2 = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            backgroundColor: "var(--app-surface)",
            px: 1.5,
            py: 1,
            border: "1px solid var(--app-border)",
            borderRadius: "6px",
            boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.15)",
            fontSize: "13px",
            fontWeight: 600,
            color: "var(--app-text)",
            whiteSpace: "nowrap",
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          {`${label} : `}
          <Typography sx={{ fontWeight: "700" }}>{payload[0].value}</Typography>
        </Box>
      );
    }
    return null;
  };

  const CustomTooltip3 = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            backgroundColor: "var(--app-surface)",
            p: "16px",
            borderRadius: "12px",
            boxShadow: "0 4px 16px rgba(0, 0, 0, 0.1)",
            fontSize: "14px",
            lineHeight: "1.8",
            color: "var(--app-text)",
            minWidth: "180px",
          }}
        >
          <Box sx={{ fontWeight: 600, fontSize: "15px", mb: "8px" }}>
            {label} :
          </Box>

          <Box component="hr" sx={{ borderTop: "1px solid var(--app-border)", my: "8px" }} />

          <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            {payload.map((entry, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  fontSize: "14px",
                }}
              >
                <Box
                  sx={{
                    width: "10px",
                    height: "10px",
                    backgroundColor: entry.color,
                    borderRadius: "50%",
                  }}
                />
                <Box component="span">
                  {entry.name} :{" "}
                  <Box component="strong">{entry.value ?? "-"}</Box>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      );
    }
    return null;
  };

  // -------------------- Date helpers --------------------
  const quickRanges = [
    ["Today", "today"],
    ["Last 7 Days", "last_7_days"],
    ["Last 30 Days", "last_30_days"],
    ["Last 3 Months", "last_3_months"],
    ["Last 6 Months", "last_6_months"],
    ["Last 12 Months", "last_12_months"],
  ];

  const applyCustomDates = () => {
    if (fromDate && toDate) {
      const startDate = new Date(fromDate).toLocaleDateString("en-US", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
      const endDate = new Date(toDate).toLocaleDateString("en-US", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
      setSelectedDateFilter("custom");
      setSelectedDateTitle(`${startDate} - ${endDate}`);
      handleCloseDateMenu();
    }
  };

  // -------------------- Transform timeSeries -> chart data --------------------
  useEffect(() => {
    if (isLoading || isLoadingTimeseries) return;

    setLineData(buildLineData(timeSeries, granularityState.value));
    setBarData(buildBarData(timeSeries, casesGranularityState.value));
    setCentreData(buildCentreData(analyticsData));
  }, [
    timeSeries,
    analyticsData,
    isLoading,
    isLoadingTimeseries,
    granularityState.value,
    casesGranularityState.value,
  ]);

  const isAllZero =
    barData && barData.length > 0 && barData.every((item) => item.count === 0);

  const isLineAllZero =
    lineData &&
    lineData.length > 0 &&
    lineData.every((item) => {
      const normal = item.normal ?? 0;
      const abnormal = item.abnormal ?? 0;
      const critical = item.critical ?? 0;
      const retake = item.retake ?? 0;
      const total = item.total ?? normal + abnormal + critical + retake;

      return (
        normal === 0 &&
        abnormal === 0 &&
        critical === 0 &&
        retake === 0 &&
        total === 0
      );
    });

  const loading = Boolean(isLoading || isLoadingTimeseries);

  const cardSx = {
    backgroundColor: "var(--app-surface)",
    borderRadius: "16px",
    border: "1px solid var(--app-border)",
    boxShadow: "var(--app-shadow)",
    overflow: "hidden",
    // Entrance + hover polish (shadow/border only — matches the main dashboard).
    opacity: 0,
    animation: "dashFadeUp .5s ease forwards",
    transition: "box-shadow .22s ease, border-color .22s ease",
    "&:hover": {
      boxShadow: "0 12px 28px rgba(16,24,40,0.12)",
      borderColor: "rgba(87,81,233,0.35)",
    },
  };

  // -------------------- Permission Based Blank Preview --------------------
  if (!hasDashboardAccess) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "var(--app-surface)", // optional
        }}
      >
        <BlankPreview
          icon={nopreview}
          title="You do not have access to this page"
          subtitle=""
        />
      </Box>
    );
  }

  // -------------------- UI --------------------
  return (
    <>
      <Helmet>
        <title>Spandan - Dashboard</title>
      </Helmet>

      <Box className="main-wrappers">
        <Box
          className="page-wrapper"
          sx={{
            maxHeight: "99vh",
            overflowY: "auto",
            pb: "48px",
            pt: "65px",
            fontFamily: "Figtree",
            backgroundColor: "var(--app-bg)",
          }}
        >
          <Box className="content">
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: { xs: 2, md: 2.5 },
                pt: 0,
                pb: { xs: 4, md: 6 },
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  overflow: "hidden",
                  borderRadius: "16px",
                  background:
                    "linear-gradient(120deg, #4B45D6 0%, #5751E9 45%, #7A74F0 100%)",
                  color: "#fff",
                  px: { xs: 2.5, md: 3 },
                  py: { xs: 2, md: 2.25 },
                  boxShadow: "0 8px 22px rgba(87,81,233,0.22)",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: -70,
                    right: -20,
                    width: 170,
                    height: 170,
                    borderRadius: "50%",
                    background: "rgba(255,255,255,0.10)",
                  },
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    bottom: -60,
                    right: 110,
                    width: 110,
                    height: 110,
                    borderRadius: "50%",
                    background: "rgba(255,255,255,0.07)",
                  },
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    zIndex: 1,
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    justifyContent: "space-between",
                    alignItems: { xs: "flex-start", sm: "center" },
                    gap: 1,
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "11.5px",
                        fontWeight: 700,
                        letterSpacing: "0.1em",
                        textTransform: "uppercase",
                        color: "rgba(255,255,255,0.75)",
                        mb: 0.25,
                        fontFamily: "Figtree",
                      }}
                    >
                      {(() => {
                        const h = new Date().getHours();
                        return h < 12
                          ? "Good morning"
                          : h < 17
                            ? "Good afternoon"
                            : "Good evening";
                      })()}
                    </Typography>

                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 800,
                        fontFamily: "Figtree",
                        fontSize: { xs: "20px", md: "23px" },
                        color: "#fff",
                        lineHeight: 1.2,
                      }}
                    >
                      Welcome, {user?.firstName} {user?.lastName}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      flexShrink: 0,
                    }}
                  >
                    <Icon
                      icon="solar:calendar-linear"
                      width="16"
                      height="16"
                      color="rgba(255,255,255,0.85)"
                    />
                    <Typography
                      sx={{
                        fontSize: 13.5,
                        fontWeight: 500,
                        color: "rgba(255,255,255,0.85)",
                        fontFamily: "Figtree",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {format(new Date(), "EEE, dd MMM yyyy")}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Filters Row — fade only (no transform) + visible overflow so the
                  inline (disablePortal) centre dropdown isn't clipped. */}
              <Box
                sx={{
                  ...cardSx,
                  animation: "dashFadeIn .5s ease forwards",
                  overflow: "visible",
                  px: { xs: 1.5, md: 2 },
                  py: { xs: 1, md: 1.25 },
                }}
              >
                <DashboardFiltersRow
                  selectedDateTitle={selectedDateTitle}
                  selectedDateFilter={selectedDateFilter}
                  setSelectedDateFilter={setSelectedDateFilter}
                  setSelectedDateTitle={setSelectedDateTitle}
                  fromDate={fromDate}
                  toDate={toDate}
                  setFromDate={setFromDate}
                  setToDate={setToDate}
                  dateMenuOpen={dateMenuOpen}
                  dateAnchorEl={dateAnchorEl}
                  handleOpenDateMenu={handleOpenDateMenu}
                  handleCloseDateMenu={handleCloseDateMenu}
                  applyCustomDates={applyCustomDates}
                  geoFilter={geoFilter}
                  setGeoFilter={setGeoFilter}
                  allCentres={allCentres}
                  selectedCentres={selectedCentres}
                  setSelectedCentres={setSelectedCentres}
                  isMdUp={isMdUp}
                  DropdownIcon={DropdownIcon}
                  handleClearFilters={handleClearFilters}
                  isDefault={isDefault}
                  handleExportCSV={handleExportCSV}
                />
              </Box>

              {/* Filter actions — mobile only; desktop keeps them in the card */}
              <DashboardFilterActions
                handleClearFilters={handleClearFilters}
                isDefault={isDefault}
                handleExportCSV={handleExportCSV}
                sx={{
                  display: { xs: "flex", md: "none" },
                  justifyContent: "flex-start",
                  mt: -0.5,
                  px: 0.5,
                }}
              />

              <Box>
                <DashboardKPISection analyticsData={analyticsData} />
              </Box>

              <DashboardPieSection
                analyticsData={analyticsData}
                INTERPRETATION_COLORS={INTERPRETATION_COLORS}
                pieChartloading={pieChartloading}
                pieSeriesData={pieSeriesData}
              />

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    lg:
                      hasInterpretationAccess && hasCasesAccess
                        ? "1fr 1fr"
                        : "1fr",
                  },
                  gap: { xs: 2, md: 2.5 },
                }}
              >
                {hasInterpretationAccess && (
                  <Box sx={{ ...cardSx, p: { xs: 1.5, md: 2.5 } }}>
                    <DashboardTestsLine
                      selectedDateFilter={selectedDateFilter}
                      granularityState={granularityState}
                      setGranularityState={setGranularityState}
                      loading={loading}
                      isLoadingTimeseries={isLoadingTimeseries}
                      lineData={lineData}
                      isLineAllZero={isLineAllZero}
                      barChartLoading={barChartLoading}
                    />
                  </Box>
                )}

                {hasCasesAccess && (
                  <Box sx={{ ...cardSx, p: { xs: 1.5, md: 2.5 } }}>
                    <DashboardCasesBar
                      selectedDateFilter={selectedDateFilter}
                      casesGranularityState={casesGranularityState}
                      setCasesGranularityState={setCasesGranularityState}
                      loading={loading}
                      isLoadingTimeseries={isLoadingTimeseries}
                      barData={barData}
                      isAllZero={isAllZero}
                      CustomTooltip2={CustomTooltip2}
                      barsLoading={barsLoading}
                    />
                  </Box>
                )}
              </Box>

              {/* -------------------- Centres & Map (optional) -------------------- */}
              {/* {centreData?.length > 0 && (
                <div style={{ marginTop: 24 }}>
                  <h6>
                    <strong>Centres Map</strong>
                  </h6>
                  {isLoaded ? (
                    <GoogleMap
                      mapContainerStyle={containerStyle}
                      center={
                        markers.length > 0 &&
                        markers[0]?.lat &&
                        markers[0]?.lng &&
                        !isNaN(markers[0].lat) &&
                        !isNaN(markers[0].lng)
                          ? {
                              lat: Number(markers[0].lat),
                              lng: Number(markers[0].lng),
                            }
                          : { lat: 20.5937, lng: 78.9629 }
                      }
                      zoom={markers.length > 0 ? 8 : 4}
                      onLoad={(map) => (mapRef.current = map)}
                      options={{
                        disableDefaultUI: true,
                        zoomControl: false,
                        streetViewControl: false,
                        fullscreenControl: false,
                      }}
                    >
                      {markers.map((marker, index) => (
                        <MarkerF
                          key={index}
                          position={{
                            lat: Number(marker.lat),
                            lng: Number(marker.lng),
                          }}
                          onClick={() => setSelectedMarkerId(index)}
                        >
                          {selectedMarkerId === index && (
                            <InfoWindowF
                              onCloseClick={() => setSelectedMarkerId(null)}
                            >
                              <div>
                                <strong>{marker.name}</strong>
                                <br />
                                {marker.address}
                              </div>
                            </InfoWindowF>
                          )}
                        </MarkerF>
                      ))}
                    </GoogleMap>
                  ) : (
                    <div>Loading map...</div>
                  )}
                </div>
              )} */}
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
}
